import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Button } from 'components';
import { FacebookIcon, GmailIcon, InstagramIcon, WhatsappIcon } from 'icons';
import serviceTopBg from '../../images/service-top-bg.png';
import serviceBottomBg from '../../images/service-bottom-bg.png';
import service1 from '../../icons/ServiceIcons/service1.svg';
import service2 from '../../icons/ServiceIcons/service2.svg';
import service3 from '../../icons/ServiceIcons/service3.svg';
import service4 from '../../icons/ServiceIcons/service4.svg';
import service5 from '../../icons/ServiceIcons/service5.svg';
import useEmblaCarousel from 'embla-carousel-react';
import ClassNames from 'embla-carousel-class-names';
import { motion } from 'framer-motion';

const ServicesGrid = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 1,
      icon: service1,
      titleKey: 'services.service1',
      defaultTitle: 'Custom External Raffstoren & Sun-Shading Systems',
    },
    {
      id: 2,
      icon: service2,
      titleKey: 'services.service2',
      defaultTitle: 'Professional Installation & Mounting Services',
    },
    {
      id: 3,
      icon: service3,
      titleKey: 'services.service3',
      defaultTitle: 'Dedicated Window Mounting Team (Mounting Only)',
    },
    {
      id: 4,
      icon: service4,
      titleKey: 'services.service4',
      defaultTitle: 'Outdoor Shading Solutions & Garden Umbrellas',
    },
    {
      id: 5,
      icon: service5,
      titleKey: 'services.service5',
      defaultTitle: 'Maintenance & Repair Services for Performance',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
      {services.map((service) => (
        <div
          key={service.id}
          className="flex flex-col items-center text-center"
        >
          <div className="w-[175px] h-[175px] mb-4">
            <img
              src={service.icon}
              alt={t(service.titleKey, service.defaultTitle)}
              className="w-full h-full object-contain"
            />
          </div>
          <h3 className="font-semibold text-gray-800 mb-2 text-sm md:text-base">
            {t(service.titleKey, service.defaultTitle)}
          </h3>
        </div>
      ))}
    </div>
  );
};

const ServicesCarousel = () => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: 'trimSnaps',
    },
    [ClassNames()],
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const services = [
    {
      id: 1,
      icon: service1,
      titleKey: 'services.service1',
      defaultTitle: 'Custom External Raffstoren & Sun-Shading Systems',
    },
    {
      id: 2,
      icon: service2,
      titleKey: 'services.service2',
      defaultTitle: 'Professional Installation & Mounting Services',
    },
    {
      id: 3,
      icon: service3,
      titleKey: 'services.service3',
      defaultTitle: 'Dedicated Window Mounting Team (Mounting Only)',
    },
    {
      id: 4,
      icon: service4,
      titleKey: 'services.service4',
      defaultTitle: 'Outdoor Shading Solutions & Garden Umbrellas',
    },
    {
      id: 5,
      icon: service5,
      titleKey: 'services.service5',
      defaultTitle: 'Maintenance & Repair Services for Performance',
    },
  ];

  const scrollTo = (index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  };

  return (
    <div className="relative">
      <div className="embla" ref={emblaRef}>
        <div className="embla__container flex">
          {services.map((service) => (
            <div
              key={service.id}
              className="embla__slide flex-[0_0_80%] md:flex-[0_0_40%] lg:flex-[0_0_30%] xl:flex-[0_0_20%] px-4"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-[175px] h-[175px] mb-4">
                  <img
                    src={service.icon}
                    alt={t(service.titleKey, service.defaultTitle)}
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2 text-sm md:text-base leading-tight whitespace-normal break-words min-h-[3rem] flex items-center w-[70%]">
                  {t(service.titleKey, service.defaultTitle)}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center mt-6 space-x-2">
        {services.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === selectedIndex
                ? 'bg-[#1E90AA] scale-110'
                : 'bg-gray-400 opacity-50 hover:opacity-75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const ServicesScreen = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const whatsappNumbers = [
    { number: '+41 79 123 45 67', link: 'https://wa.me/41791234567' },
    { number: '+41 78 898 02 84', link: 'https://wa.me/41788980284' },
  ];

  return (
    <>
      <Helmet>
        <title>Sonnen Raffstoren GmbH — Services</title>
        <meta
          name="description"
          content="Discover our comprehensive services including installation, maintenance, and customization of blinds, Markisen, Insektenschutz and shutters."
        />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="Sonnen Raffstoren GmbH — Services" />
        <meta
          property="og:description"
          content="Professional services for blinds and shutters: installation, maintenance, and more."
        />
        <div style={{ display: 'none' }}>
          <h1>Raffstoren — Professionelle Montage und Anpassung</h1>
          Raffstoren sind außenliegende Jalousien mit präziser Lichtsteuerung,
          die optimalen Sonnenschutz bieten. Unsere Montageservices umfassen
          maßgeschneiderte Installation, Anpassung an die Architektur und
          Integration moderner Steuerungssysteme für maximale Energieeffizienz.
          <h2>Markisen — Flexible Beschattungslösungen</h2>
          Markisen bieten elegante Beschattung für Terrassen und Außenbereiche.
          Wir installieren hochwertige Markisen mit verschiedenen Größen und
          Designs, die perfekt an Ihre Bedürfnisse angepasst werden und
          langfristigen Schutz vor Sonne und UV-Strahlen gewährleisten.
          <h3>Insektenschutz — Komplette Montage und Wartung</h3>
          Insektenschutzsysteme halten Insekten draußen und lassen frische Luft
          herein. Unsere Services umfassen die fachgerechte Montage von
          Insektenschutzgittern, die nahtlose Integration mit Raffstoren und
          Markisen sowie regelmäßige Wartung für dauerhafte Funktionalität.
          <h1>Professionelle Dienstleistungen für Sonnenschutz</h1>
          Sonnen Raffstoren GmbH bietet umfassende Dienstleistungen von der
          Beratung bis zur Wartung. Unsere erfahrenen Teams führen präzise
          Vor-Ort-Messungen durch, planen individuelle Lösungen und sorgen für
          eine fachgerechte Montage aller Beschattungssysteme.
          <h2>Wartung und Reparatur von Raffstoren</h2>
          Regelmäßige Wartung ist entscheidend für die Langlebigkeit Ihrer
          Raffstoren. Wir bieten professionelle Wartungs- und Reparaturdienste,
          die sicherstellen, dass Ihre Beschattungssysteme optimal funktionieren
          und ihre Energieeffizienz über Jahre hinweg erhalten bleibt.
          <h3>Beratung und Planung für Schweizer Gebäude</h3>
          Als Spezialisten für die Schweiz beraten wir Sie umfassend zu
          geeigneten Beschattungslösungen. Von der ersten Planung über die
          Auswahl der Materialien bis zur finalen Installation begleiten wir
          jedes Projekt mit Schweizer Präzision und Qualität.
        </div>
      </Helmet>
      <div className="min-h-screen">
        <motion.div
          className="min-h-svh flex items-center bg-cover bg-bottom"
          style={{ backgroundImage: `url(${serviceTopBg})` }}
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[200px] w-full grid md:grid-cols-[70%_30%] gap-8 text-white">
            {/* Left Side */}
            <div className="flex flex-col justify-center">
              <h2 className="text-[32px] md:text-[42px] leading-none font-bold mb-6">
                {t('services.mainTitle', 'Professional Services')}
              </h2>
              <p className="mb-8">
                {t(
                  'services.description',
                  'Discover the excellence of Sonnen Raffstoren GmbH, your trusted partner for sun-shading solutions in Switzerland. We specialize in external raffstoren, blinds, and innovative shading systems combining Swiss precision with modern design. From consultation to installation and maintenance, our certified team delivers unparalleled quality. Whether you need energy-efficient window coverings for your home or commercial installations, we provide personalized service that exceeds expectations.',
                )}
              </p>
              <Button
                text={t('services.contactButton', 'Contact Us')}
                className="w-fit bg-primary md:bg-white text-white md:text-black"
                onClick={() => (window.location.href = '/contact')}
              />
            </div>
            {/* Right Side - Swiss Quality */}
            <div className="relative hidden lg:flex justify-end">
              <div className="absolute bottom-0 right-0 rounded-b-[40px] h-[1000vh] w-[100px] bg-white flex items-end justify-center">
                <p className="text-[#1E90AA] font-bold text-[32px] md:text-[42px] leading-none transform -rotate-90 whitespace-nowrap pl-[300px]">
                  Swiss Quality
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* What We Offer Section */}
      <motion.div
        className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[200px] text-center"
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
      >
        <h2 className="text-[32px] md:text-[42px] leading-none font-bold text-gray-800 mb-8">
          {t('services.whatWeOfferTitle', 'What We Offer?')}
        </h2>
        <p className="text-gray-600 leading-relaxed">
          {t(
            'services.whatWeOfferText',
            'At Sonnen Raffstoren GmbH, we pride ourselves on exceptional quality in every blinds and shutter solution. Our services cover consultation, custom design, professional installation, and maintenance. We create energy-efficient window coverings that enhance aesthetics and provide thermal insulation. Our expert team works closely with you to meet your specific needs. Using premium materials, we ensure durability across residential and commercial projects. Swiss precision means every detail is carefully considered. We offer personalized solutions with comprehensive warranties and support. Choose us for unparalleled expertise and superior service.',
          )}
        </p>
      </motion.div>

      {/* Services Icons Section */}
      <motion.div
        className="bg-white mt-8"
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto px-4">
          {/* Mobile: Carousel, Desktop: Grid */}
          <div className="block md:hidden">
            <ServicesCarousel />
          </div>
          <div className="hidden md:block">
            <ServicesGrid />
          </div>
        </div>

        {/* Services Bottom Section */}
        <motion.div
          className="min-h-svh flex items-center bg-cover bg-top pt-[300px] relative"
          style={{ backgroundImage: `url(${serviceBottomBg})` }}
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black"></div>
          <div className="px-smallPx md:px-mediumPx lg:px-largePx xl:px-[200px] w-full grid md:grid-cols-[70%_30%] gap-8 text-white relative z-10">
            {/* Left Side */}
            <div className="flex flex-col justify-center pb-[100px]">
              <h2 className="text-[32px] md:text-[42px] leading-none font-bold mb-6">
                {t(
                  'services.bottomTitle',
                  'Consultation, Planning & Custom Solutions',
                )}
              </h2>
              <div className="space-y-6">
                <p className="leading-relaxed">
                  {t(
                    'services.bottomText1',
                    'Sonnen Raffstoren specializes in delivering high-quality sun-shading and installation solutions for residential and commercial buildings throughout Switzerland. Our focus is on combining functionality, design, and precision to create comfortable spaces that perform reliably in all conditions.',
                  )}
                </p>
                <p className="leading-relaxed">
                  {t(
                    'services.bottomText2',
                    'External raffstoren are at the core of our offering. Each system is custom-made to match the architecture of the building, allowing precise control of daylight, heat, and privacy. A wide range of colors, lamella designs, and control options ensures seamless integration with both modern and traditional façades.',
                  )}
                </p>
                <p className="leading-relaxed">
                  {t(
                    'services.bottomText3',
                    "Every project begins with professional consultation and careful planning. We take the time to understand the building, its usage, and the client's expectations before proposing a tailored solution. Accurate on-site measurements and technical expertise allow us to deliver shading systems that function perfectly.",
                  )}
                </p>
                <p className="leading-relaxed">
                  {t(
                    'services.bottomText4',
                    'Installation is carried out by experienced specialists who work with precision and attention to detail. Our team ensures that every system is mounted cleanly, safely, and efficiently, guaranteeing long-term durability and reliable operation.',
                  )}
                </p>
                <p className="leading-relaxed">
                  {t(
                    'services.bottomText5',
                    'Beyond indoor comfort, we extend our expertise to outdoor spaces with elegant shading solutions, including umbrellas for gardens, terraces, and commercial environments. We also provide comprehensive maintenance and repair services to ensure lasting performance.',
                  )}
                </p>
              </div>
            </div>
            {/* Right Side - Social Media */}
            <div className="relative hidden lg:flex justify-end">
              <div className="absolute bottom-0 right-0 rounded-t-[40px] h-[70vh] w-[100px] bg-gradient-to-b from-white via-white to-black flex flex-col items-center justify-between pt-20">
                <div className="flex flex-col space-y-4">
                  <a
                    href="https://www.facebook.com/sonnenraffstoren"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-800 hover:text-blue-600 transition-colors"
                  >
                    <div className="w-10 h-10">
                      <FacebookIcon color="#1E90AA" />
                    </div>
                  </a>
                  <a
                    href="https://www.instagram.com/sonnenraffstoren"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-800 hover:text-pink-600 transition-colors"
                  >
                    <div className="w-10 h-10">
                      <InstagramIcon color="#1E90AA" />
                    </div>
                  </a>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="text-gray-800 hover:text-green-600 transition-colors"
                  >
                    <div className="w-10 h-10">
                      <WhatsappIcon color="#1E90AA" />
                    </div>
                  </button>
                  <a
                    href="mailto:info@sonnenraffstoren.ch"
                    className="text-gray-800 hover:text-red-600 transition-colors"
                  >
                    <div className="w-10 h-10">
                      <GmailIcon color="#1E90AA" />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* WhatsApp Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Contact via WhatsApp
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {whatsappNumbers.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-primary text-white text-center py-2 px-4 rounded hover:bg-primary/50 transition-colors"
                >
                  {item.number}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
